import { prettifyErrors } from 'common/helpers';
import { BtlApplicationFormPageEnum, SectionProblems } from 'pages/BtlApplicationFormPage/types';
import { getAvailablePagesForStatus } from './statusPages';
import type { BuildBtlCaseSidebarParams } from '../types';
import { CaseSidebarItemId, CaseSidebarSection } from '../types';

const FINANCING_MISSING_FIELD_KEYS = new Set([
  'purchasePrice',
  'financingAmount',
  'grossMonthlyRentalPayment',
  'paymentProfile',
  'termInMonths',
  'feesAddedToBalance',
  'outstandingBalance',
  'expectedPropertyType',
  'repaymentStrategy',
  'repaymentStrategyDetails',
]);

const PROPERTY_MISSING_FIELD_KEYS = new Set([
  'propertyAddress',
  'tenure',
  'propertyType',
  'propertySubType',
  'estateAgent',
  'numberOfBedrooms',
  'hasCommunalSpace',
  'numberOfUnits',
  'yearsRemainingOnLease',
]);

const ITEM_TO_PROBLEMS_SECTION: Partial<Record<CaseSidebarItemId, BtlApplicationFormPageEnum>> = {
  [CaseSidebarItemId.FINANCING]: BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY,
  [CaseSidebarItemId.PROPERTY]: BtlApplicationFormPageEnum.FINANCING_AND_PROPERTY,
  [CaseSidebarItemId.PRODUCT]: BtlApplicationFormPageEnum.PRODUCT,
  [CaseSidebarItemId.TOP_SLICING]: BtlApplicationFormPageEnum.TOP_SLICING,
  [CaseSidebarItemId.AFFORDABILITY_CHECK]: BtlApplicationFormPageEnum.AFFORDABILITY_CHECK,
  [CaseSidebarItemId.SUBMIT_DIP]: BtlApplicationFormPageEnum.SUBMIT_DIP,
  [CaseSidebarItemId.SOLICITORS]: BtlApplicationFormPageEnum.SOLICITORS,
  [CaseSidebarItemId.BANK_DETAILS]: BtlApplicationFormPageEnum.BANK_DETAILS,
  [CaseSidebarItemId.COMMUNICATION]: BtlApplicationFormPageEnum.COMMUNICATION,
  [CaseSidebarItemId.CUSTOMER_FILES]: BtlApplicationFormPageEnum.DOCUMENTS,
  [CaseSidebarItemId.EVIDENCE]: BtlApplicationFormPageEnum.DOCUMENTS,
  [CaseSidebarItemId.VALUATION]: BtlApplicationFormPageEnum.VALUATION,
  [CaseSidebarItemId.POST_OFFER]: BtlApplicationFormPageEnum.POST_OFFER,
  [CaseSidebarItemId.SUBMIT_APPLICATION]: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
  [CaseSidebarItemId.ADDITIONAL_INFORMATION]: BtlApplicationFormPageEnum.ADDITIONAL_INFORMATION,
};

function shouldResolveMissingFields(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): boolean {
  if (!context.missingFields) {
    return false;
  }

  switch (itemId) {
    case CaseSidebarItemId.PRODUCT:
    case CaseSidebarItemId.TOP_SLICING:
    case CaseSidebarItemId.AFFORDABILITY_CHECK:
      return Boolean(context.application.product);
    case CaseSidebarItemId.SUBMIT_DIP:
    case CaseSidebarItemId.SOLICITORS:
    case CaseSidebarItemId.BANK_DETAILS:
    case CaseSidebarItemId.COMMUNICATION:
    case CaseSidebarItemId.CUSTOMER_FILES:
    case CaseSidebarItemId.EVIDENCE:
    case CaseSidebarItemId.VALUATION:
    case CaseSidebarItemId.POST_OFFER:
    case CaseSidebarItemId.SUBMIT_APPLICATION:
    case CaseSidebarItemId.ADDITIONAL_INFORMATION:
      return Boolean(context.isAffordabilityPassed);
    default:
      return true;
  }
}

function shouldSplitFinancingAndPropertyWarnings(context: BuildBtlCaseSidebarParams): boolean {
  const pages = getAvailablePagesForStatus(context.application.status);

  return pages[CaseSidebarSection.APPLICATION].includes(CaseSidebarItemId.PROPERTY);
}

function filterMissingFieldKeys(
  itemId: CaseSidebarItemId,
  context: BuildBtlCaseSidebarParams,
  missingFieldKeys: string[],
): string[] {
  if (!shouldSplitFinancingAndPropertyWarnings(context)) {
    return missingFieldKeys;
  }

  if (itemId === CaseSidebarItemId.FINANCING) {
    return missingFieldKeys.filter(field => FINANCING_MISSING_FIELD_KEYS.has(field));
  }

  if (itemId === CaseSidebarItemId.PROPERTY) {
    return missingFieldKeys.filter(field => PROPERTY_MISSING_FIELD_KEYS.has(field));
  }

  return missingFieldKeys;
}

function extractSectionMissingFieldWarnings(
  section: BtlApplicationFormPageEnum,
  missingFields: SectionProblems,
  formatMessage: BuildBtlCaseSidebarParams['formatMessage'],
  itemId?: CaseSidebarItemId,
  context?: BuildBtlCaseSidebarParams,
): string[] {
  const fieldsToSkip = section === BtlApplicationFormPageEnum.PRODUCT ? ['isCurrentProductSuitable'] : [];
  const sectionData = missingFields[section];

  if (!sectionData) {
    return [];
  }

  const resolveMissingFieldKeys = (missingFieldKeys: string[]) => {
    if (!itemId || !context) {
      return missingFieldKeys;
    }

    return filterMissingFieldKeys(itemId, context, missingFieldKeys);
  };

  const warnings: string[] = [];

  if (section === BtlApplicationFormPageEnum.PRODUCT && !Array.isArray(sectionData)) {
    warnings.push(
      ...prettifyErrors({ missingFields: resolveMissingFieldKeys(sectionData.missingFields) }, fieldsToSkip).flat(),
    );

    if (!sectionData.isCurrentProductSuitable) {
      warnings.push(formatMessage({ id: 'btlApplicationForm.PRODUCT.notSuitable.title' }));
    }

    return warnings;
  }

  if (Array.isArray(sectionData)) {
    return sectionData
      .flatMap(item => prettifyErrors({ missingFields: resolveMissingFieldKeys(item.missingFields) }, fieldsToSkip))
      .flat();
  }

  return prettifyErrors({ missingFields: resolveMissingFieldKeys(sectionData.missingFields) }, fieldsToSkip).flat();
}

function resolveApplicantsMissingFieldWarnings(context: BuildBtlCaseSidebarParams): string[] {
  const { missingFields, application } = context;

  if (!missingFields) {
    return [];
  }

  const warnings: string[] = [];

  const problems = missingFields as SectionProblems;
  const individuals = problems[BtlApplicationFormPageEnum.APPLICANTS_INDIVIDUAL];

  if (Array.isArray(individuals)) {
    individuals.forEach((item, index) => {
      const errors = prettifyErrors(item, []).flat();
      const individual = application.borrower?.individuals[index];
      const label = individual ? `${individual.firstName} ${individual.lastName}` : undefined;

      errors.forEach(error => {
        warnings.push(label ? `${label}: ${error}` : error);
      });
    });
  }

  const companies = problems[BtlApplicationFormPageEnum.APPLICANTS_COMPANY];

  if (Array.isArray(companies)) {
    companies.forEach((item, index) => {
      const errors = prettifyErrors(item, []).flat();
      const company = application.borrower?.companies[index];
      const label = company?.companyName;

      errors.forEach(error => {
        warnings.push(label ? `${label}: ${error}` : error);
      });
    });
  }

  return warnings;
}

function resolveTopSlicingMissingFieldWarnings(context: BuildBtlCaseSidebarParams): string[] {
  const section = BtlApplicationFormPageEnum.TOP_SLICING;
  const warnings = extractSectionMissingFieldWarnings(
    section,
    context.missingFields as SectionProblems,
    context.formatMessage,
  );

  const householdsToHasIndividuals = context.householdsToHasIndividuals;

  if (householdsToHasIndividuals) {
    const householdsThatNeedIndividuals = Object.entries(householdsToHasIndividuals)
      .filter(([, value]) => value === false)
      .map(([key]) => key);

    if (householdsThatNeedIndividuals.length > 0) {
      warnings.push(context.formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.noIndividuals.tooltip' }));
    }
  }

  return warnings;
}

export function resolveMissingFieldsWarnings(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): string[] {
  if (!shouldResolveMissingFields(itemId, context)) {
    return [];
  }

  if (itemId === CaseSidebarItemId.APPLICANTS) {
    return resolveApplicantsMissingFieldWarnings(context);
  }

  if (itemId === CaseSidebarItemId.TOP_SLICING) {
    return resolveTopSlicingMissingFieldWarnings(context);
  }

  const section = ITEM_TO_PROBLEMS_SECTION[itemId];

  if (!section) {
    return [];
  }

  return extractSectionMissingFieldWarnings(
    section,
    context.missingFields as SectionProblems,
    context.formatMessage,
    itemId,
    context,
  );
}

export function resolvePropertyStatusWarnings(itemId: CaseSidebarItemId, context: BuildBtlCaseSidebarParams): string[] {
  if (itemId !== CaseSidebarItemId.PROPERTY || !shouldSplitFinancingAndPropertyWarnings(context)) {
    return [];
  }

  if (context.application.applicationProperty?.status !== 'REJECTED') {
    return [];
  }

  return [context.formatMessage({ id: 'caseSidebar.warnings.propertyRejected' })];
}
